import { useMemo, useState } from "react";
import {
  Form,
  Modal,
  Row,
  Col,
  Checkbox,
  Button,
  Flex,
  Divider,
  Radio,
} from "antd";

import ChevronLeftIcon from "@/icons/ChevronLeftIcon";
import RefreshIcon from "@/icons/RefreshIcon";

import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";
import TaskDurationInfo from "./components/TaskDurationInfo";
import NumberInput from "./components/NumberInput";
import ResultInfo from "./components/ResultInfo";

import { getPluralizedWord } from "@/utils";

import {
  LIMITS_OPTIONS,
  FREQUENCY_OPTIONS,
  WEEKS_OPTIONS,
  PLURALIZATION_WORDS,
} from "@/constants/repeats";

import { useTaskDuration } from "./hooks/useTaskDuration";
import { useFormConfig } from "./hooks/useFormConfig";

const RepeatModal = ({ task, data, visible, onCancel, onSave }) => {
  // Используем хук для расчета продолжительности задачи
  const { taskDurationDays, taskDurationWorksDays } = useTaskDuration(task);

  // Используем хук для конфигурации формы
  const { initialValues } = useFormConfig(data);

  //   Форма периодичности
  const [form] = Form.useForm();
  // Состояние валидности формы
  const [isFormValid, setIsFormValid] = useState(true);

  //   Подписки на изменения полей формы
  const limits = Form.useWatch("limits", form);
  const frequency = Form.useWatch("frequency", form);
  const start_date = Form.useWatch("start_date", form);
  const n_count = Form.useWatch("n_count", form);
  const repeat_count = Form.useWatch("repeat_count", form);
  const f_count = Form.useWatch("f_count", form);
  const g_count = Form.useWatch("g_count", form);
  const start_next_task = Form.useWatch("start_next_task", form);
  const monthly_frequency_option = Form.useWatch(
    "monthly_frequency_option",
    form
  );

  const onValuesChange = async (changedValues) => {
    if ("n_count" in changedValues) {
      const newNCount = changedValues.n_count;

      if (newNCount < taskDurationDays - (g_count || 0)) {
        form.setFieldsValue({ g_count: null });

        if (start_next_task === "day_before_due_date")
          form.setFieldsValue({ start_next_task: null });
      }

      if (
        newNCount < taskDurationDays &&
        (start_next_task === "after_due_date" ||
          start_next_task === "after_completion")
      ) {
        form.setFieldsValue({ start_next_task: null });
      }
    }

    if ("frequency" in changedValues) {
      const newFrequency = changedValues.frequency;

      if (newFrequency !== "daily")
        form.setFieldsValue({ start_next_task: "day_before_start_new" });
      else form.setFieldsValue({ start_next_task: null });

      form.setFieldsValue({ g_count: null });
      form.setFieldsValue({ f_count: null });
    }
    if ("start_next_task" in changedValues) {
      if (changedValues.start_next_task !== "day_before_due_date")
        form.setFieldsValue({ g_count: null });
      if (changedValues.start_next_task !== "day_before_start_new")
        form.setFieldsValue({ f_count: null });
    }

    setTimeout(() => {
      checkFormValidity();
    }, 0);
  };

  // Проверка валидности формы для отображения ResultInfo
  const checkFormValidity = async () => {
    // Проверяем только определенные поля
    const fieldsToValidate = ["limits", "frequency"];

    // Добавляем условные поля в зависимости от выбранных значений
    if (limits === "end_date") {
      fieldsToValidate.push("end_date");
    }
    if (limits === "repeat_count") {
      fieldsToValidate.push("repeat_count");
    }
    if (frequency === "daily") {
      fieldsToValidate.push("n_count");
    }
    if (frequency === "weekly") {
      fieldsToValidate.push("m_count", "weeks_days");
    }
    if (frequency === "monthly") {
      fieldsToValidate.push("monthly_frequency_option");
      if (monthly_frequency_option === "day_of_month") {
        fieldsToValidate.push(
          "monthly_frequency_val_1",
          "monthly_frequency_val_2"
        );
      }
      if (monthly_frequency_option === "day_of_week_of_month") {
        fieldsToValidate.push(
          "monthly_frequency_val_3",
          "monthly_frequency_val_4",
          "monthly_frequency_val_5"
        );
      }
    }
    if (frequency === "yearly") {
      fieldsToValidate.push(
        "frequency_by_year_val_1",
        "frequency_by_year_val_2",
        "frequency_by_year_val_3"
      );
    }

    try {
      await form.validateFields(fieldsToValidate, { validateOnly: true });
      setIsFormValid(true);
    } catch (error) {
      setIsFormValid(false);
    }
  };

  const startNextTaskOptions = useMemo(() => {
    return [
      frequency === "daily" && taskDurationDays && n_count >= taskDurationDays
        ? {
            label: (
              <div style={{ lineHeight: "30px" }}>
                По истечении срока исполнения текущей задачи
              </div>
            ),
            value: "after_due_date",
          }
        : null,
      frequency === "daily" && n_count >= taskDurationDays
        ? {
            label: (
              <div style={{ lineHeight: "30px" }}>
                По завершении текущей задачи
              </div>
            ),
            value: "after_completion",
          }
        : null,
      {
        label: (
          <Flex align="center" gap={6}>
            <span>За</span>
            <NumberInput
              name="f_count"
              label="Дней"
              min={0}
              required={false}
              rules={[
                {
                  required: start_next_task === "day_before_start_new",
                  message: "",
                },
              ]}
            />
            <span>
              {getPluralizedWord(f_count, PLURALIZATION_WORDS.DAYS)} до даты
              начала создаваемой задачи
            </span>
          </Flex>
        ),
        value: "day_before_start_new",
      },
      frequency === "daily" && taskDurationDays
        ? {
            label: (
              <Flex align="center" gap={6}>
                <span>За</span>
                <NumberInput
                  name="g_count"
                  min={Math.max(0, taskDurationDays - n_count)}
                  required={false}
                  rules={[
                    {
                      required: start_next_task === "day_before_due_date",
                      message: "",
                    },
                  ]}
                />

                <span>
                  {getPluralizedWord(g_count, PLURALIZATION_WORDS.DAYS)} до даты
                  исполнения текущей задачи
                </span>
              </Flex>
            ),
            value: "day_before_due_date",
          }
        : null,
    ].filter(Boolean);
  }, [frequency, taskDurationDays, n_count, f_count, g_count, start_next_task]);

  const handleCancel = () => {
    // Запоминаем значения формы при отмене
    onCancel(form.getFieldsValue());
  };

  const handleSave = async (values) => {
    onSave(values);
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  return (
    <Modal
      onOk={onOk}
      onCancel={handleCancel}
      title={
        <Flex align="center" gap={8}>
          <Button
            icon={<ChevronLeftIcon style={{ fontSize: 16 }} />}
            onClick={() => handleCancel()}
          />
          <span>Правила повторения</span>
        </Flex>
      }
      open={visible}
      width={600}
      okText="Подтвердить"
      cancelText="Назад"
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSave}
        onValuesChange={onValuesChange}
      >
        <TaskDurationInfo
          task={task}
          taskDurationDays={taskDurationDays}
          taskDurationWorksDays={taskDurationWorksDays}
        />

        <Form.Item
          name="start_date"
          label="Начать"
          required={false}
          rules={[{ required: true, message: "Укажите дату" }]}
        >
          <DigitsDatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Divider style={{ borderColor: "var(--color-gray)" }} />

        <Form.Item name="limits" label="Пределы повторений">
          <Radio.Group block options={LIMITS_OPTIONS} optionType="button" />
        </Form.Item>

        {limits === "end_date" && (
          <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }} align="middle">
            <Col span={12}>
              <Form.Item
                name="end_date"
                label=""
                style={{ marginBottom: 0 }}
                required={false}
                rules={[
                  { required: limits === "end_date", message: "Укажите дату" },
                ]}
              >
                <DigitsDatePicker
                  minDate={start_date}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {limits === "repeat_count" && (
          <Flex align="center" gap={6}>
            <RefreshIcon
              style={{ fontSize: 20, color: "var(--color-gray-m)" }}
            />
            <span>После</span>
            <NumberInput
              name="repeat_count"
              required={false}
              rules={[{ required: limits === "repeat_count", message: "" }]}
            />
            <span>
              {getPluralizedWord(repeat_count, PLURALIZATION_WORDS.REPEATS)}
            </span>
          </Flex>
        )}

        <Divider style={{ borderColor: "var(--color-gray)" }} />

        <Form.Item name="frequency" label="Частота">
          <Radio.Group block options={FREQUENCY_OPTIONS} optionType="button" />
        </Form.Item>

        {frequency === "daily" && (
          <Flex vertical gap={7}>
            <Flex align="center" gap={6}>
              <span>Каждый</span>
              <NumberInput
                name="n_count"
                required={false}
                rules={[{ required: frequency === "daily", message: "" }]}
              />
              <span>день</span>
            </Flex>
            <Form.Item
              name="only_workdays"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>По будням</Checkbox>
            </Form.Item>
          </Flex>
        )}
        {frequency === "weekly" && (
          <Flex vertical gap={7}>
            <Flex align="center" gap={6}>
              <span>Каждую</span>
              <NumberInput
                name="m_count"
                required={false}
                rules={[{ required: frequency === "weekly", message: "" }]}
              />
              <span>неделю</span>
            </Flex>

            <Form.Item
              name="weeks_days"
              label=""
              style={{ marginBottom: 0, paddingTop: 10 }}
              required={false}
              rules={[
                { required: true, message: "Выберите хотя бы один день" },
              ]}
            >
              <Checkbox.Group
                options={WEEKS_OPTIONS}
                className="weeklyCheckbox"
              />
            </Form.Item>

            <Form.Item
              name="only_workdays"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>По будням</Checkbox>
            </Form.Item>
          </Flex>
        )}
        {frequency === "monthly" && (
          <Flex vertical gap={7}>
            <Form.Item
              name="monthly_frequency_option"
              label=""
              style={{ marginBottom: 0 }}
            >
              <Radio.Group
                options={[
                  {
                    label: (
                      <Flex align="center" gap={6}>
                        <NumberInput
                          name="monthly_frequency_val_1"
                          min={1}
                          max={31}
                          required={false}
                          rules={[
                            {
                              required:
                                frequency === "monthly" &&
                                monthly_frequency_option === "day_of_month",
                              message: "",
                            },
                          ]}
                        />
                        <span>число каждого</span>
                        <NumberInput
                          name="monthly_frequency_val_2"
                          min={1}
                          required={false}
                          rules={[
                            {
                              required:
                                frequency === "monthly" &&
                                monthly_frequency_option === "day_of_month",
                              message: "",
                            },
                          ]}
                        />
                        <span>месяца</span>
                      </Flex>
                    ),
                    value: "day_of_month",
                  },
                  {
                    label: (
                      <Flex align="center" gap={6}>
                        <Form.Item
                          name="monthly_frequency_val_3"
                          label=""
                          style={{ marginBottom: 0, width: 70 }}
                          // Иначе пропускает клик по лейблу и закрывает опции селекта
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <CustomSelect
                            options={WEEKS_OPTIONS}
                            allowClear={false}
                          />
                        </Form.Item>
                        <span>каждой</span>
                        <NumberInput
                          name="monthly_frequency_val_4"
                          min={1}
                          required={false}
                          rules={[
                            {
                              required:
                                frequency === "monthly" &&
                                monthly_frequency_option ===
                                  "day_of_week_of_month",
                              message: "",
                            },
                          ]}
                        />
                        <span>недели каждого</span>
                        <NumberInput
                          name="monthly_frequency_val_5"
                          min={1}
                          required={false}
                          rules={[
                            {
                              required:
                                frequency === "monthly" &&
                                monthly_frequency_option ===
                                  "day_of_week_of_month",
                              message: "",
                            },
                          ]}
                        />
                        <span>месяца</span>
                      </Flex>
                    ),
                    value: "day_of_week_of_month",
                  },
                ]}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              />
            </Form.Item>
            <Form.Item
              name="only_workdays"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>По будням</Checkbox>
            </Form.Item>
          </Flex>
        )}
        {frequency === "yearly" && (
          <Flex vertical gap={7}>
            <Flex align="center" gap={6}>
              <NumberInput
                name="frequency_by_year_val_1"
                min={1}
                max={31}
                required={false}
                rules={[
                  {
                    required: frequency === "yearly",
                    message: "",
                  },
                ]}
              />
              <span>число каждого</span>
              <NumberInput
                name="frequency_by_year_val_2"
                min={1}
                required={false}
                rules={[
                  {
                    required: frequency === "yearly",
                    message: "",
                  },
                ]}
              />
              <span>месяца каждого</span>
              <NumberInput
                name="frequency_by_year_val_3"
                min={1}
                required={false}
                rules={[
                  {
                    required: frequency === "yearly",
                    message: "",
                  },
                ]}
              />
              <span>года</span>
            </Flex>
            <Form.Item
              name="only_workdays"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>По будням</Checkbox>
            </Form.Item>
          </Flex>
        )}
        <Divider style={{ borderColor: "var(--color-gray)" }} />

        <Form.Item
          name="start_next_task"
          label="Следующая задача появится"
          required={false}
          rules={[{ required: true, message: "Выберите вариант" }]}
        >
          <Radio.Group
            options={startNextTaskOptions}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          />
        </Form.Item>

        {isFormValid && <ResultInfo form={form} />}
      </Form>
    </Modal>
  );
};

export default RepeatModal;
